import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/datatable/datatable";
import SearchComponent from "../../components/search";
import BtwStatusPill from "../../components/btw-status-pill";
import { customerFetchPartnerBtwApplications } from "../../core/axios/endpoints";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");

// Jira-lozenges: neutraal / info / succes / gevaar (designbrief).
const INVOICE_STATUS_LABELS = {
  pending: { label: "Nog niet gefactureerd", color: "bg-[#091E420F] text-ink-subtle" },
  invoiced: { label: "Gefactureerd", color: "bg-brand-subtle text-brand" },
  paid: { label: "Betaald", color: "bg-[#DCFFF1] text-[#1F845A]" },
  credited: { label: "Gecrediteerd", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

const selectClass =
  "h-8 rounded border-2 border-line bg-white px-2 text-sm text-ink focus:border-brand focus:outline-none";

const PartnerBtwTeruggavesPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterWorkflowId, setFilterWorkflowId] = useState("");
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState("");

  const statusMap = useMemo(() => {
    const m = {};
    statuses.forEach((s) => { m[String(s._id)] = s; });
    return m;
  }, [statuses]);

  const fetchData = useCallback(
    async ({ page, pageSize, resetPageSize }, search = searchText) => {
      setLoading(true);
      try {
        const body = await customerFetchPartnerBtwApplications({
          page,
          limit: pageSize,
          search,
        });
        setStatuses(body?.statuses ?? []);
        setData({
          data: (body?.docs ?? []).map((item, index) => ({ ...item, id: index })),
          totalDataCount: body?.pagination?.totalDocs ?? 0,
          resetPageSize,
        });
      } catch (e) {
        console.error("Partner BTW lijst ophalen mislukt:", e);
      } finally {
        setLoading(false);
      }
    },
    [searchText],
  );

  useEffect(() => {
    fetchData({ page: 1, pageSize: 25, resetPageSize: false });
  }, []);

  const handleSearch = (q) => {
    setSearchText(q);
    fetchData({ page: 1, pageSize: 25, resetPageSize: true }, q);
  };

  const columns = [
    {
      title: "Naam",
      dataIndex: "step1",
      render: (step1) => {
        const naam = `${step1?.voorletters ?? ""} ${step1?.tussenvoegsel ?? ""} ${step1?.achternaam ?? ""}`
          .replace(/\s+/g, " ")
          .trim();
        return <span className="font-medium text-ink">{naam || "-"}</span>;
      },
    },
    {
      title: "Plaats",
      dataIndex: "step1",
      render: (step1) => (
        <span className="text-sm text-ink-subtle">
          {step1?.postcode ?? ""} {step1?.plaatsnaam ?? ""}
        </span>
      ),
    },
    {
      title: "Aanvraag",
      dataIndex: "createdAt",
      render: (v) => <span className="text-sm">{fmtDate(v)}</span>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Installatie",
      dataIndex: "step3",
      render: (step3) => <span className="text-sm">{fmtDate(step3?.installationDate)}</span>,
    },
    {
      title: "Status",
      dataIndex: "workflowStatusId",
      render: (id) => {
        const status = id ? statusMap[String(id)] : null;
        return <BtwStatusPill status={status} size="sm" />;
      },
    },
    {
      title: "Factuur",
      dataIndex: "partnerInvoice",
      render: (pi) => {
        const cfg = INVOICE_STATUS_LABELS[pi?.status ?? "pending"];
        return (
          <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${cfg.color}`}>
            {cfg.label}
          </span>
        );
      },
    },
  ];

  const filteredCount = (data?.data ?? []).filter((row) => {
    if (filterWorkflowId && String(row.workflowStatusId?._id ?? row.workflowStatusId) !== filterWorkflowId) return false;
    if (filterInvoiceStatus && (row.partnerInvoice?.status ?? "pending") !== filterInvoiceStatus) return false;
    return true;
  }).length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <header className="mb-6">
          <h1 className="text-2xl font-medium text-ink">BTW Teruggaves</h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            Overzicht van alle BTW-teruggave aanvragen die via uw bedrijf zijn binnengekomen.
          </p>
        </header>

        {/* Toolbar: zoeken + filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="w-full max-w-xs">
            <SearchComponent onSearch={handleSearch} type={"partner-btw"} />
          </div>
          <select
            value={filterWorkflowId}
            onChange={(e) => setFilterWorkflowId(e.target.value)}
            className={selectClass}
          >
            <option value="">Alle workflow-statussen</option>
            {statuses.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            value={filterInvoiceStatus}
            onChange={(e) => setFilterInvoiceStatus(e.target.value)}
            className={selectClass}
          >
            <option value="">Alle factuur-statussen</option>
            {Object.entries(INVOICE_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          {(filterWorkflowId || filterInvoiceStatus) ? (
            <button
              type="button"
              onClick={() => {
                setFilterWorkflowId("");
                setFilterInvoiceStatus("");
              }}
              className="h-8 rounded px-2.5 text-xs font-medium text-brand hover:underline"
            >
              Wis filters
            </button>
          ) : null}
          <span className="ml-auto text-xs text-ink-subtlest">
            {`${filteredCount} van ${data?.data?.length ?? 0}`}
          </span>
        </div>

        <div className="table-responsive rounded bg-white shadow-card">
          <Table
            columns={columns}
            response={{
              ...data,
              data: (data?.data ?? []).filter((row) => {
                if (filterWorkflowId && String(row.workflowStatusId?._id ?? row.workflowStatusId) !== filterWorkflowId) return false;
                if (filterInvoiceStatus && (row.partnerInvoice?.status ?? "pending") !== filterInvoiceStatus) return false;
                return true;
              }),
            }}
            fetchData={fetchData}
            loading={loading}
            onRowClick={(record) => {
              if (record?._id) {
                navigate(`/panel/customer/btw-teruggaves/${record._id}`);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PartnerBtwTeruggavesPage;
